import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, BookOpen, Edit, Save, X } from 'lucide-react';
import { useLeagueStore } from '../../lib/leagueStore';
import { useUserStore } from '../../lib/userStore';
import { useEmbedStore } from '../../lib/embedStore';
import { EmbedBackButton } from '../shared/EmbedBackButton';
import { RichTextEditor, RichTextContent, sanitizeRichText, isRichTextEmpty } from '../shared/RichTextEditor';
import { supabase } from '../../lib/supabase';
import { usePageTracking } from '../../lib/hooks/usePageTracking';
import toast from 'react-hot-toast';
import './LeagueRulesEmbed.css';

const ARSENAL_FONTS_URL =
  'https://fonts.googleapis.com/css2?family=Anton&family=Open+Sans:wght@400;600;700&display=swap';

function useArsenalFonts(enabled: boolean) {
  useEffect(() => {
    if (!enabled) return;
    const id = 'arsenal-fonts';
    if (document.getElementById(id)) return;
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = ARSENAL_FONTS_URL;
    document.head.appendChild(link);
  }, [enabled]);
}

export function LeagueRules() {
  // Tracker cette page
  usePageTracking('myoffice');

  const navigate = useNavigate();
  const selectedLeague = useLeagueStore((state) => state.selectedLeague);
  const currentUserId = useUserStore((state) => state.user_id);
  const isEmbed = useEmbedStore((state) => state.isEmbed);
  useArsenalFonts(isEmbed);
  
  const [rules, setRules] = useState<string>('');
  const [isCommissioner, setIsCommissioner] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedRules, setEditedRules] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!selectedLeague) {
      navigate('/dashboard');
      return;
    }

    fetchLeagueRules();
    checkCommissionerStatus();
  }, [selectedLeague, currentUserId]);

  const fetchLeagueRules = async () => {
    if (!selectedLeague?.id) return;

    try {
      const { data, error } = await supabase
        .from('league_settings')
        .select('speceficruleexplication')
        .eq('league_id', selectedLeague.id)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Erreur lors de la récupération des règles:', error);
        toast.error('Erreur lors du chargement des règles');
        return;
      }

      setRules(data?.speceficruleexplication || '');
    } catch (error) {
      console.error('Erreur lors de la récupération des règles:', error);
      toast.error('Erreur lors du chargement des règles');
    } finally {
      setLoading(false);
    }
  };

  const checkCommissionerStatus = async () => {
    if (!selectedLeague?.id || !currentUserId) return;

    try {
      const { data, error } = await supabase
        .from('leagues')
        .select('admin_user_id, admin_user_id2')
        .eq('league_id', selectedLeague.id)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Erreur lors de la vérification du statut de commissaire:', error);
        return;
      }

      const isAdmin = data?.admin_user_id === Number(currentUserId) || 
                      data?.admin_user_id2 === Number(currentUserId);
      setIsCommissioner(isAdmin);
    } catch (error) {
      console.error('Erreur lors de la vérification du statut de commissaire:', error);
    }
  };

  const handleStartEdit = () => {
    setEditedRules(rules);
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setEditedRules('');
    setIsEditing(false);
  };

  const handleSaveRules = async () => {
    if (!selectedLeague?.id) return;

    setSaving(true);
    try {
      // Sanitisation défensive avant persistance
      const cleaned = sanitizeRichText(editedRules);
      const payload = isRichTextEmpty(cleaned) ? '' : cleaned;

      const { error } = await supabase
        .from('league_settings')
        .update({ speceficruleexplication: payload })
        .eq('league_id', selectedLeague.id);

      if (error) {
        console.error('Erreur lors de la sauvegarde des règles:', error);
        toast.error('Erreur lors de la sauvegarde des règles');
        return;
      }

      setRules(payload);
      setIsEditing(false);
      toast.success('Règles mises à jour avec succès');
    } catch (error) {
      console.error('Erreur lors de la sauvegarde des règles:', error);
      toast.error('Erreur lors de la sauvegarde des règles');
    } finally {
      setSaving(false);
    }
  };

  if (!selectedLeague) {
    return null;
  }

  return (
    <div className={`card shadow-lg border-0${isEmbed ? ' league-rules-embed' : ''}`}>
      <div className="card-body p-5">
        <EmbedBackButton />
        {/* Header */}
        {isEmbed ? (
          <div className="lre-header">
            <div className="lre-header__eyebrow">{selectedLeague.name}</div>
            <h1 className="lre-header__title">Règles du pool</h1>
            {isCommissioner && !isEditing && (
              <button
                className="btn btn-primary d-flex align-items-center gap-2 mt-3"
                onClick={handleStartEdit}
              >
                <Edit size={16} />
                Modifier
              </button>
            )}
          </div>
        ) : (
        <div className="d-flex align-items-center mb-4">
          <button
            className="btn btn-outline-secondary me-3"
            onClick={() => navigate('/my-office')}
          >
            <ArrowLeft size={20} />
          </button>
          <div className="flex-grow-1">
            <div className="d-flex align-items-center">
              <BookOpen className="h-8 w-8 text-primary me-3" />
              <div>
                <h2 className="card-title mb-1"> Règles/Infos Pool de la Ligue</h2>
                <p className="text-muted mb-0">{selectedLeague.name}</p>
              </div>
            </div>
          </div>
          {isCommissioner && !isEditing && (
            <button
              className="btn btn-primary d-flex align-items-center gap-2"
              onClick={handleStartEdit}
            >
              <Edit size={18} />
              Modifier
            </button>
          )}
        </div>
        )}

        {/* Content */}
        <div className="row">
          <div className="col-12">
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Chargement...</span>
                </div>
              </div>
            ) : (
              <>
                {isEditing ? (
                  /* Mode édition */
                  <div>
                    <div className="mb-3">
                      <label htmlFor="rulesTextarea" className="form-label fw-bold">
                        Règles spécifiques de votre ligue
                      </label>
                      <RichTextEditor
                        value={editedRules}
                        onChange={setEditedRules}
                        variant={isEmbed ? 'embed' : 'default'}
                        placeholder="Décrivez ici les règles spécifiques de votre ligue, les particularités du système de pointage, les règles de transaction, les conditions de draft, etc."
                        ariaLabel="Éditeur de règles de la ligue"
                        disabled={saving}
                      />
                      <div className="form-text">
                        Utilisez la barre d'outils pour mettre le texte en gras, italique, souligné, ajouter des titres, des listes ou des liens.
                      </div>
                    </div>

                    {/* Boutons d'action */}
                    <div className="d-flex gap-2">
                      <button
                        className="btn btn-primary d-flex align-items-center gap-2"
                        onClick={handleSaveRules}
                        disabled={saving}
                      >
                        {saving ? (
                          <>
                            <div className="spinner-border spinner-border-sm" role="status">
                              <span className="visually-hidden">Sauvegarde...</span>
                            </div>
                            Sauvegarde...
                          </>
                        ) : (
                          <>
                            <Save size={18} />
                            Sauvegarder
                          </>
                        )}
                      </button>
                      <button
                        className="btn btn-outline-secondary d-flex align-items-center gap-2"
                        onClick={handleCancelEdit}
                        disabled={saving}
                      >
                        <X size={18} />
                        Annuler
                      </button>
                    </div>
                  </div>
                ) : (
                  /* Mode affichage */
                  <div>
                    {rules ? (
                      <div className="bg-light p-4 rounded">
                        <RichTextContent
                          html={rules}
                          variant={isEmbed ? 'embed' : 'default'}
                        />
                      </div>
                    ) : (
                      <div className="text-center py-5">
                        <BookOpen size={48} className="text-muted mb-3" />
                        <h4 className="text-muted">Aucune règle personnalisée définie</h4>
                        <p className="text-muted">
                          {isCommissioner
                            ? "En tant que commissaire, vous pouvez définir des règles spécifiques pour votre ligue."
                            : "Le commissaire n'a pas encore défini de règles spécifiques pour cette ligue."
                          }
                        </p>
                        {isCommissioner && (
                          <button
                            className="btn btn-primary mt-3"
                            onClick={handleStartEdit}
                          >
                            <Edit size={18} className="me-2" />
                            Ajouter des règles
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
